import DiscourseService from '../services/discourse.service.js';
import DiscourseMessageBus from '../services/discourse.messageBus.js';

const postLikeUsersRequests = {};
const postLikeUsersRequestVersions = {};
const likeToggleRequests = {};

function appendAvatarCacheBuster(avatarTemplate, cacheBuster) {
  if (!avatarTemplate || !cacheBuster) return avatarTemplate;
  const sanitized = String(avatarTemplate).replace(/([?&])v=[^&]*/g, '');
  const separator = sanitized.includes('?') ? '&' : '?';
  return `${sanitized}${separator}v=${encodeURIComponent(String(cacheBuster))}`;
}

export const discourse = {
  namespaced: true,
  
  state: {
    categories: [],
    topics: [],
    topicsBookmarks: [], // Lista de bookmarks
    communityActivityTopics: [], // Lista específica para a seção "Atividades da Comunidade"
    currentTopic: null,
    currentPost: null,
    currentCategory: null,
    loading: false,
    error: null,
    messageBusConnected: false,
    notifications: [],
    initialized: false,
    discourseUsername: null,
    avatarCacheBuster: null,
    postLikeUsersByPostId: {}
  },

  mutations: {
    SET_CATEGORIES(state, categories) {
      state.categories = categories;
    },
    SET_TOPICS(state, topics) {
      state.topics = topics;
    },
    SET_TOPICS_BOOKMARKS(state, topics) { // Nova mutation para bookmarks
      state.topicsBookmarks = topics;
    },
    SET_COMMUNITY_ACTIVITY_TOPICS(state, topics) {
      state.communityActivityTopics = topics;
    },
    ADD_TOPIC(state, topic) {
      state.topics.unshift(topic);
    },
    UPDATE_TOPIC(state, updatedTopic) {
      const index = state.topics.findIndex(t => t.id === updatedTopic.id);
      if (index !== -1) {
        state.topics[index] = updatedTopic;
      }
    },
    SET_CURRENT_TOPIC(state, topic) {
      state.currentTopic = topic;
    },
    SET_CURRENT_POST(state, post) {
      state.currentPost = post;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_MESSAGE_BUS_CONNECTED(state, connected) {
      state.messageBusConnected = connected;
    },
    ADD_NOTIFICATION(state, notification) {
      state.notifications.unshift(notification);
    },
    CLEAR_NOTIFICATIONS(state) {
      state.notifications = [];
    },
    SET_INITIALIZED(state, initialized) {
      state.initialized = initialized;
    },
    SET_DISCOURSE_USERNAME(state, username) {
      state.discourseUsername = username;
    },
    SET_AVATAR_CACHE_BUSTER(state, cacheBuster) {
      state.avatarCacheBuster = cacheBuster || null;
    },
    SET_POST_LIKE_USERS(state, { postId, users }) {
      state.postLikeUsersByPostId = {
        ...state.postLikeUsersByPostId,
        [postId]: users
      };
    },
    SET_CURRENT_CATEGORY(state, category) {
      state.currentCategory = category;
    },
    // Mutation para atualizar um post específico dentro do tópico atual (optimistic update)
    UPDATE_POST_IN_TOPIC(state, { postId, updates }) {
      if (state.currentTopic?.post_stream?.posts) {
        const postIndex = state.currentTopic.post_stream.posts.findIndex(p => p.id === postId);
        if (postIndex !== -1) {
          state.currentTopic.post_stream.posts = state.currentTopic.post_stream.posts.map(post => {
            if (post.id !== postId) return post;
            return {
              ...post,
              ...updates
            };
          });
        }
      }
    },
    APPLY_OPTIMISTIC_AVATAR_UPDATE(state, { username, cacheBuster }) {
      if (!username || !cacheBuster) return;
      const normalizedUsername = String(username).toLowerCase();
      const updatePostAvatar = (post) => {
        if (!post?.username || String(post.username).toLowerCase() !== normalizedUsername) {
          return post;
        }
        return {
          ...post,
          avatar_template: appendAvatarCacheBuster(post.avatar_template, cacheBuster)
        };
      };

      if (state.currentTopic?.post_stream?.posts?.length) {
        state.currentTopic.post_stream.posts = state.currentTopic.post_stream.posts.map(updatePostAvatar);
      }

      if (state.currentPost) {
        state.currentPost = updatePostAvatar(state.currentPost);
      }
    }
  },

  actions: {
    // ========================================
    // INITIALIZATION
    // ========================================
    async initialize({ commit, rootState, dispatch }) {
      // Pega a URL do Discourse da variável de ambiente
      const discourseUrl = import.meta.env.VITE_DISCOURSE_API_URL || '';
      
      // Pega a API key do usuário do store de auth
      const user = rootState.auth?.user;
      const userApiKey = user?.discourseApiKey || user?.member?.discourseToken;
      const userApiUsername = user?.discourseUsername || user?.member?.discourseUsername;
      
      // Pega a URL do Message Bus da variável de ambiente
      const messageBusUrl = import.meta.env.VITE_DISCOURSE_MESSAGE_BUS_URL || '';
      
      // Inicializa os services
      DiscourseService.initialize(discourseUrl, userApiKey, userApiUsername);
      DiscourseMessageBus.initialize(messageBusUrl, userApiKey, null);
      
      // Busca o username do Discourse via API
      try {
        const response = await DiscourseService.getCurrentUser();
        const username = response.data?.current_user?.username;
        if (username) {
          commit('SET_DISCOURSE_USERNAME', username);
        }
      } catch (error) {
        console.warn('Não foi possível buscar username do Discourse:', error);
      }
      
      commit('SET_INITIALIZED', true);
    },

    // ========================================
    // EMOJIS
    // ========================================
    async fetchEmojis({ commit, dispatch }) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const response = await DiscourseService.getEmojis();
        const emojis = response.data || {};
        return Promise.resolve(emojis);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao carregar emojis';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // ========================================
    // CATEGORIES
    // ========================================
    async fetchCategories({ commit, dispatch }) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const response = await DiscourseService.getCategories();
        const categories = response.data.category_list?.categories || [];
        commit('SET_CATEGORIES', categories);
        return Promise.resolve(categories);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao carregar categorias';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchCategory({ commit, dispatch }, categoryId) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      try {
        const response = await DiscourseService.getCategory(categoryId);
        return Promise.resolve(response.data);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao carregar categoria';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchCategoryTopics({ commit, dispatch, state }, { categoryId, params = {} }) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        // Buscar o slug da categoria se disponível
        let categorySlug = null;
        
        // Tentar pegar do currentCategory se já estiver carregado
        if (state.currentCategory && state.currentCategory.slug) {
          categorySlug = state.currentCategory.slug;
        } else {
          // Buscar no array de categories
          const category = state.categories.find(cat => cat.id === categoryId);
          if (category && category.slug) {
            categorySlug = category.slug;
          }
        }
        
        // Adicionar slug aos params se encontrado
        const requestParams = { ...params };
        if (categorySlug) {
          requestParams.categorySlug = categorySlug;
        }
        
        const response = await DiscourseService.getCategoryTopics(categoryId, requestParams);
        const category = response.data.category;
        const topics = response.data.topic_list?.topics || [];
        
        commit('SET_CURRENT_CATEGORY', category);
        commit('SET_TOPICS', topics);
        
        return Promise.resolve({ category, topics });
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao carregar categoria';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // ========================================
    // TOPICS
    // ========================================
    async fetchTopics({ commit, dispatch }, params = {}) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const response = await DiscourseService.getTopics(params);
        const topics = response.data.topic_list?.topics || [];
        commit('SET_TOPICS', topics);
        return Promise.resolve(topics);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao carregar tópicos';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchLatestTopics({ commit, dispatch }, params = {}) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const response = await DiscourseService.getLatestTopics(params);
        const topics = response.data.topic_list?.topics || [];
        commit('SET_TOPICS', topics);
        return Promise.resolve(topics);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao carregar tópicos recentes';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchLatestPostsPreview(_, { limit = 6 } = {}) {
      try {
        const response = await DiscourseService.getLatestPosts();
        const posts = response.data?.latest_posts || [];
        return posts.slice(0, limit);
      } catch (error) {
        return [];
      }
    },

    async fetchUnreadTopics({ commit, dispatch }, params = {}) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const response = await DiscourseService.getUnreadTopics(params);
        const topics = response.data.topic_list?.topics || [];
        commit('SET_COMMUNITY_ACTIVITY_TOPICS', topics);
        return Promise.resolve(topics);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao carregar tópicos não lidos';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchTopTopics({ commit, dispatch }, { period = 'monthly', categoryId = null } = {}) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const response = await DiscourseService.getTopTopics(period, categoryId);
        const topics = response.data.topic_list?.topics || [];
        commit('SET_COMMUNITY_ACTIVITY_TOPICS', topics);
        return Promise.resolve(topics);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao carregar tópicos em destaque';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchBookmarks({ commit, dispatch, state }, params = {}) {
      await dispatch('ensureInitialized');
      
      if (!state.discourseUsername) {
        const errorMessage = 'Username do Discourse não encontrado';
        commit('SET_ERROR', errorMessage);
        // Limpar tópicos de bookmarks quando não há username
        commit('SET_TOPICS_BOOKMARKS', []);
        return Promise.reject(new Error(errorMessage));
      }

      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const response = await DiscourseService.getBookmarks(state.discourseUsername, params);
        // A estrutura de bookmarks é user_bookmark_list.bookmarks
        const bookmarks = response.data.user_bookmark_list?.bookmarks || [];
        
        // Verificar se há bookmarks
        if (!bookmarks || bookmarks.length === 0) {
          commit('SET_TOPICS_BOOKMARKS', []);
          return Promise.resolve([]);
        }
        
        // Transformar bookmarks em formato similar a topics para compatibilidade
        const topicsMap = new Map(); // Usar Map para evitar duplicatas por topic_id
        
        bookmarks.forEach(bookmark => {
          // Validar se bookmark tem topic_id
          if (!bookmark.topic_id) {
            console.warn('Bookmark sem topic_id ignorado:', bookmark);
            return;
          }
          
          const topicId = bookmark.topic_id;
          const topicData = {
            id: topicId,
            topic_id: topicId,
            title: bookmark.title || bookmark.fancy_title || 'Sem título',
            fancy_title: bookmark.fancy_title || bookmark.title,
            slug: bookmark.slug,
            category_id: bookmark.category_id,
            posts_count: bookmark.highest_post_number || 0,
            reply_count: bookmark.highest_post_number > 0 ? bookmark.highest_post_number - 1 : 0,
            views: 0, // Bookmarks não retornam views
            bumped_at: bookmark.bumped_at,
            created_at: bookmark.created_at,
            last_posted_at: bookmark.bumped_at || bookmark.created_at,
            bookmarked: true,
            bookmark_id: bookmark.id,
            bookmarkable_id: bookmark.bookmarkable_id,
            bookmarkable_type: bookmark.bookmarkable_type
          };
          
          // Se já existe um tópico com esse topic_id, manter o mais recente
          if (topicsMap.has(topicId)) {
            const existingTopic = topicsMap.get(topicId);
            
            // Comparar bumped_at primeiro, depois created_at como critério de desempate
            const existingBumpedAt = existingTopic.bumped_at ? new Date(existingTopic.bumped_at) : null;
            const newBumpedAt = bookmark.bumped_at ? new Date(bookmark.bumped_at) : null;
            const existingCreatedAt = existingTopic.created_at ? new Date(existingTopic.created_at) : null;
            const newCreatedAt = bookmark.created_at ? new Date(bookmark.created_at) : null;
            
            let shouldReplace = false;
            
            // Se ambos têm bumped_at, comparar
            if (existingBumpedAt && newBumpedAt) {
              if (newBumpedAt > existingBumpedAt) {
                shouldReplace = true;
              } else if (newBumpedAt.getTime() === existingBumpedAt.getTime()) {
                // Se bumped_at é igual, usar created_at como critério de desempate
                if (existingCreatedAt && newCreatedAt && newCreatedAt > existingCreatedAt) {
                  shouldReplace = true;
                }
              }
            } else if (newBumpedAt && !existingBumpedAt) {
              // Novo tem bumped_at mas existente não
              shouldReplace = true;
            } else if (!newBumpedAt && existingBumpedAt) {
              // Existente tem bumped_at mas novo não, manter existente
              shouldReplace = false;
            } else {
              // Nenhum tem bumped_at, comparar created_at
              if (existingCreatedAt && newCreatedAt && newCreatedAt > existingCreatedAt) {
                shouldReplace = true;
              }
            }
            
            if (shouldReplace) {
              topicsMap.set(topicId, topicData);
            }
          } else {
            // Primeira ocorrência deste topic_id
            topicsMap.set(topicId, topicData);
          }
        });
        
        // Converter Map para Array e ordenar por bumped_at (mais recente primeiro)
        const topics = Array.from(topicsMap.values()).sort((a, b) => {
          const dateA = new Date(a.bumped_at || a.created_at || 0);
          const dateB = new Date(b.bumped_at || b.created_at || 0);
          return dateB - dateA; // Mais recente primeiro
        });
        
        commit('SET_TOPICS_BOOKMARKS', topics);
        return Promise.resolve(topics);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao carregar tópicos salvos';
        commit('SET_ERROR', errorMessage);
        // Limpar tópicos de bookmarks quando houver erro ou quando não houver bookmarks
        commit('SET_TOPICS_BOOKMARKS', []);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async fetchTopic({ commit, dispatch }, topicId) {
      await dispatch('ensureInitialized');
      commit('SET_CURRENT_TOPIC', null);
      commit('SET_LOADING', true);
      try {
        const response = await DiscourseService.getTopic(topicId, { include_raw: true, print: true });
        const topic = response.data;
        commit('SET_CURRENT_TOPIC', topic);
        
        // Marcar todos os posts do tópico como lidos após carregar
        // Criar objeto timings com todos os posts (1000ms por post como tempo mínimo)
        const posts = topic.post_stream?.posts || [];
        if (posts.length > 0) {
          const timings = {};
          posts.forEach(post => {
            if (post.post_number) {
              // Usar 1000ms (1 segundo) como tempo mínimo de leitura por post
              timings[post.post_number.toString()] = 1000;
            }
          });
          
          // Calcular tempo total no tópico (soma dos timings)
          const topicTime = Object.values(timings).reduce((sum, time) => sum + time, 0);
          
          try {
            DiscourseService.markTopicAsRead(topicId, timings, topicTime);
          } catch (readError) {
            // Não falhar o carregamento do tópico se marcar como lido falhar
            console.warn('Não foi possível marcar tópico como lido:', readError);
          }
        }
        
        return Promise.resolve(topic);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao carregar tópico';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createTopic({ commit, dispatch }, topicData) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      try {
        const response = await DiscourseService.createTopic(topicData);
        // A resposta contém um post com topic_id, não o tópico completo
        // O componente que chama deve recarregar os dados após sucesso
        return Promise.resolve(response.data);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao criar tópico';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // ========================================
    // POSTS
    // ========================================
    async fetchPost({ commit, dispatch }, postId) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      try {
        const response = await DiscourseService.getPost(postId);
        const post = response.data;
        commit('SET_CURRENT_POST', post);
        return Promise.resolve(post);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao carregar post';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createPost({ commit, dispatch }, postData) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      try {
        const response = await DiscourseService.createPost(postData);
        return Promise.resolve(response.data);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao criar post';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async updatePost({ commit, dispatch }, { postId, content }) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      try {
        const response = await DiscourseService.updatePost(postId, content);
        return Promise.resolve(response.data);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao atualizar post';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async deletePost({ commit, dispatch }, postId) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      try {
        await DiscourseService.deletePost(postId);
        return Promise.resolve();
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao deletar post';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // ========================================
    // LIKES
    // ========================================
    async toggleLike({ commit, dispatch, state }, { postId, topicId }) {
      await dispatch('ensureInitialized');
      
      if (!state.discourseUsername) {
        const errorMessage = 'Username do Discourse não encontrado';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(new Error(errorMessage));
      }

      // Encontrar o post no state atual (sem fazer HTTP)
      const post = state.currentTopic?.post_stream?.posts?.find(p => p.id === postId);
      if (!post) {
        const errorMessage = 'Post não encontrado';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(new Error(errorMessage));
      }

      const requestKey = String(postId);
      if (likeToggleRequests[requestKey]) {
        return Promise.resolve({ success: true, skipped: true });
      }
      likeToggleRequests[requestKey] = true;

      // Verificar se já tem like através do actions_summary
      const originalActionsSummary = post.actions_summary || [];
      const likeAction = originalActionsSummary.find(action => action.id === 2); // 2 = Like
      const isLiked = likeAction?.acted || false;

      // Optimistic update - atualiza UI imediatamente
      const newLikeCount = isLiked 
        ? Math.max((likeAction?.count || 1) - 1, 0) 
        : (likeAction?.count || 0) + 1;
      
      const updatedActionsSummary = likeAction
        ? originalActionsSummary.map(a => 
            a.id === 2 ? { ...a, acted: !isLiked, count: newLikeCount, can_act: isLiked } : a
          )
        : [...originalActionsSummary, { id: 2, acted: true, count: 1, can_act: false }];

      commit('UPDATE_POST_IN_TOPIC', {
        postId,
        updates: { actions_summary: updatedActionsSummary }
      });

      try {
        if (isLiked) {
          // Se já tem like, remover
          await DiscourseService.deleteLike(postId);
        } else {
          // Se não tem like, adicionar
          await DiscourseService.createLike(postId);
        }

        try {
          await dispatch('fetchPostLikeUsers', { postId, force: true });
        } catch (likeUsersError) {
          console.warn('Não foi possível atualizar avatares de curtidas:', likeUsersError);
        }
        
        return Promise.resolve({ success: true });
      } catch (error) {
        // Se falhar, reverte o optimistic update
        commit('UPDATE_POST_IN_TOPIC', {
          postId,
          updates: { actions_summary: originalActionsSummary }
        });
        
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao alterar like';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        delete likeToggleRequests[requestKey];
      }
    },

    async fetchPostLikeUsers({ commit, dispatch, state }, { postId, force = false } = {}) {
      await dispatch('ensureInitialized');

      if (!postId) {
        return Promise.resolve([]);
      }

      if (!force && state.postLikeUsersByPostId[postId]) {
        return Promise.resolve(state.postLikeUsersByPostId[postId]);
      }

      if (!force && postLikeUsersRequests[postId]) {
        return postLikeUsersRequests[postId];
      }

      try {
        const requestVersion = (postLikeUsersRequestVersions[postId] || 0) + 1;
        postLikeUsersRequestVersions[postId] = requestVersion;

        postLikeUsersRequests[postId] = DiscourseService.getPostActionUsers(postId, 2)
          .then(response => {
            const users = response.data?.post_action_users || [];
            if (postLikeUsersRequestVersions[postId] !== requestVersion) {
              return users;
            }
            commit('SET_POST_LIKE_USERS', { postId, users });
            return users;
          })
          .finally(() => {
            if (postLikeUsersRequestVersions[postId] === requestVersion) {
              delete postLikeUsersRequests[postId];
            }
          });

        return postLikeUsersRequests[postId];
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // ========================================
    // BOOKMARKS
    // ========================================
    async toggleBookmark({ commit, dispatch, state }, { postId, topicId, name = null, reminderAt = null }) {
      await dispatch('ensureInitialized');
      
      if (!state.discourseUsername) {
        const errorMessage = 'Username do Discourse não encontrado';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(new Error(errorMessage));
      }

      // Encontrar o post no state atual (sem fazer HTTP)
      const post = state.currentTopic?.post_stream?.posts?.find(p => p.id === postId);
      if (!post) {
        const errorMessage = 'Post não encontrado';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(new Error(errorMessage));
      }

      const isBookmarked = post.bookmarked || false;

      // Optimistic update - atualiza UI imediatamente
      commit('UPDATE_POST_IN_TOPIC', {
        postId,
        updates: { bookmarked: !isBookmarked }
      });

      try {
        if (isBookmarked) {
          // Se já está bookmarkado, remover
          await DiscourseService.deleteBookmark(postId);
        } else {
          // Se não está bookmarkado, criar
          await DiscourseService.createBookmark(postId, reminderAt, name);
        }
        
        return Promise.resolve({ success: true });
      } catch (error) {
        // Se falhar, reverte o optimistic update
        commit('UPDATE_POST_IN_TOPIC', {
          postId,
          updates: { bookmarked: isBookmarked }
        });
        
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao alterar bookmark';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      }
    },

    // ========================================
    // UPLOADS
    // ========================================
    async uploadFile({ commit, dispatch }, file) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      try {
        const response = await DiscourseService.uploadFile(file);
        return Promise.resolve(response.data);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao fazer upload';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async syncUserAvatar(
      { commit, dispatch, state },
      { file, username = null, routeContext = null, activityFilter = 'new' } = {}
    ) {
      await dispatch('ensureInitialized');
      
      const discourseUsername = username || state.discourseUsername;
      if (!discourseUsername) {
        const errorMessage = 'Username do Discourse não encontrado';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(new Error(errorMessage));
      }

      commit('SET_LOADING', true);
      try {
        const uploadResponse = await DiscourseService.uploadFile(file, 'avatar');
        const uploadData = uploadResponse.data;
        const uploadId = uploadData?.id;
        
        if (!uploadId) {
          throw new Error('Upload ID não retornado pelo Discourse');
        }
        
        await DiscourseService.updateUserAvatar(discourseUsername, uploadId);
        const refreshResult = await dispatch('refreshAvatarEverywhere', {
          uploadId,
          username: discourseUsername,
          routeContext,
          activityFilter
        });

        return Promise.resolve({ success: true, uploadId, ...refreshResult });
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro ao sincronizar avatar com Discourse';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async refreshCurrentRouteData({ dispatch }, { routeContext = null, activityFilter = 'new' } = {}) {
      const routeName = routeContext?.name;
      if (!routeName) {
        return Promise.resolve();
      }

      if (routeName === 'forum-post') {
        const topicId = parseInt(routeContext?.params?.id, 10);
        if (Number.isFinite(topicId)) {
          await dispatch('fetchTopic', topicId);
        }
        return Promise.resolve();
      }

      if (routeName === 'forum-category' || routeName === 'forum-subcategory') {
        const categoryId = parseInt(routeContext?.params?.id, 10);
        if (Number.isFinite(categoryId)) {
          await dispatch('fetchCategoryTopics', {
            categoryId,
            params: { per_page: 20, order: 'created', ascending: 'false' }
          });
          await dispatch('fetchTopTopics', { period: 'monthly', categoryId });
          if (activityFilter === 'saved') {
            await dispatch('fetchBookmarks', { limit: 100 });
          }
        }
        return Promise.resolve();
      }

      if (routeName === 'forum') {
        await dispatch('fetchLatestTopics', { limit: 20 });
        await dispatch('fetchTopTopics', { period: 'monthly', categoryId: null });
        if (activityFilter === 'saved') {
          await dispatch('fetchBookmarks', { limit: 100 });
        }
      }

      return Promise.resolve();
    },

    async refreshAvatarEverywhere(
      { commit, dispatch, state },
      { uploadId = null, username = null, routeContext = null, activityFilter = 'new' } = {}
    ) {
      await dispatch('ensureInitialized');
      const cacheBuster = uploadId || Date.now();
      commit('SET_AVATAR_CACHE_BUSTER', cacheBuster);

      let discourseUsername = username || state.discourseUsername;
      try {
        const currentUserResponse = await DiscourseService.getCurrentUser();
        const currentUsername = currentUserResponse?.data?.current_user?.username;
        if (currentUsername) {
          discourseUsername = currentUsername;
          commit('SET_DISCOURSE_USERNAME', currentUsername);
        }
      } catch (error) {
        console.warn('Não foi possível atualizar current_user após troca de avatar:', error);
      }

      if (discourseUsername) {
        commit('APPLY_OPTIMISTIC_AVATAR_UPDATE', { username: discourseUsername, cacheBuster });
      }

      await dispatch('refreshCurrentRouteData', { routeContext, activityFilter });

      return Promise.resolve({ cacheBuster, discourseUsername });
    },

    // ========================================
    // SEARCH
    // ========================================
    async search({ commit, dispatch }, { query, params = {} }) {
      await dispatch('ensureInitialized');
      commit('SET_LOADING', true);
      try {
        const response = await DiscourseService.search(query, params);
        return Promise.resolve(response.data);
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.join(', ') || error.message || 'Erro na busca';
        commit('SET_ERROR', errorMessage);
        return Promise.reject(error);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // ========================================
    // MESSAGE BUS
    // ========================================
    async connectMessageBus({ commit, dispatch, rootState }, userId = null) {
      await dispatch('ensureInitialized');
      
      // Se não tiver userId, tenta pegar do store
      if (!userId) {
        const user = rootState.auth?.user;
        userId = user?.id || user?.member?.id || null;
      }

      try {
        await DiscourseMessageBus.connect(userId);
        commit('SET_MESSAGE_BUS_CONNECTED', true);
        
        // Subscrever a atualizações de tópicos
        DiscourseMessageBus.subscribe('/latest', (data) => {
          dispatch('handleTopicUpdate', data);
        });

        // Subscrever a notificações
        DiscourseMessageBus.subscribeToNotifications((data) => {
          commit('ADD_NOTIFICATION', data);
        });

        return Promise.resolve();
      } catch (error) {
        commit('SET_ERROR', 'Erro ao conectar MessageBus');
        return Promise.reject(error);
      }
    },

    disconnectMessageBus({ commit }) {
      DiscourseMessageBus.disconnect();
      commit('SET_MESSAGE_BUS_CONNECTED', false);
    },

    handleTopicUpdate({ commit, state }, data) {
      // Atualizar tópico se estiver na lista
      if (data.topic) {
        commit('UPDATE_TOPIC', data.topic);
      }
    },

    // ========================================
    // HELPER
    // ========================================
    async ensureInitialized({ state, dispatch, rootState }) {
      const user = rootState.auth?.user;
      const userApiKey = user?.discourseApiKey || user?.member?.discourseToken;
      DiscourseService.updateApiKey(userApiKey);
      if (!state.initialized) {
        await dispatch('initialize');
      }
    }
  },

  getters: {
    categories: (state) => state.categories,
    topics: (state) => state.topics,
    topicsBookmarks: (state) => state.topicsBookmarks,
    communityActivityTopicsStore: (state) => state.communityActivityTopics,
    currentTopic: (state) => state.currentTopic,
    currentPost: (state) => state.currentPost,
    currentCategory: (state) => state.currentCategory,
    loading: (state) => state.loading,
    error: (state) => state.error,
    messageBusConnected: (state) => state.messageBusConnected,
    notifications: (state) => state.notifications,
    unreadNotificationsCount: (state) => {
      return state.notifications.filter(n => !n.read).length;
    },
    initialized: (state) => state.initialized,
    discourseUsername: (state) => state.discourseUsername,
    avatarCacheBuster: (state) => state.avatarCacheBuster,
    postLikeUsersByPostId: (state) => state.postLikeUsersByPostId
  }
};
